import { Avatar, Box, Button, Icon, Link, mediaQueries, PageSpinner } from '@formic/components';
import React, { FC, MouseEvent, ReactNode, useState } from 'react';
import { User, UserPlus } from 'react-feather';

import { useAuthService } from './AuthContext';

interface HeaderUserStatusProps {
  children?: ReactNode;
  loginEnabled?: boolean;
  registerEnabled?: boolean;
}

const HeaderUserStatus: FC<HeaderUserStatusProps> = ({
  children,
  loginEnabled,
  registerEnabled,
}) => {
  const { login, register, logout, user } = useAuthService();
  const [isPending, setIsPending] = useState(false);

  const handleLink = (evt: MouseEvent<HTMLAnchorElement>) => {
    evt.preventDefault();
    setIsPending(true);
    void (user ? logout() : login()).finally(() => setIsPending(false));
  };

  const handleLogin = () => {
    setIsPending(true);
    void login().finally(() => setIsPending(false));
  };

  const handleRegister = () => {
    setIsPending(true);
    void register().finally(() => setIsPending(false));
  };

  return (
    <Box
      as="section"
      aria-label="Login Status"
      css={[
        () => {
          return {
            display: 'flex',
            alignItems: 'center',

            '> button > div > div': {
              [`${mediaQueries.small}`]: {
                display: 'none',
              },
            },
          };
        },
      ]}
    >
      {isPending && (
        <Box aria-live="polite" fontSize="small" marginRight="8px" role="status">
          <PageSpinner />
          Connecting to sign-in service...
        </Box>
      )}
      {user ? (
        <>
          <Box marginRight="8px" display="flex" flexDirection="column">
            <Box
              fontSize="small"
              css={(theme) => ({
                color: theme.header?.color
                  ? theme.header.color
                  : theme.fonts?.color
                  ? theme.fonts.color
                  : theme.colors.black,
              })}
            >
              {user ? user.profile.name || user.profile.preferred_username : 'Not logged in'}
            </Box>
            <Box fontSize="small" marginLeft="auto">
              <Link to="#" onClick={handleLink}>
                {isPending ? 'Signing out...' : user ? 'Logout' : 'Login'}
              </Link>
            </Box>
          </Box>
          <Avatar marginRight="8px" bg={user ? 'primary.main' : 'neutral.300'}>
            <Icon component={User} css={user && ((theme) => ({ color: theme.colors.white }))} />
          </Avatar>
        </>
      ) : (
        <>
          {loginEnabled && (
            <Button
              color="primary"
              disabled={isPending}
              onClick={handleLogin}
              height={32}
              marginRight="8px"
              iconStart={<User />}
            >
              {isPending ? 'Signing you in...' : 'Login'}
            </Button>
          )}

          {loginEnabled && registerEnabled && (
            <>
              <Box
                css={(theme) => ({
                  color: theme.header?.color
                    ? theme.header.color
                    : theme.fonts?.color
                    ? theme.fonts.color
                    : theme.colors.black,
                })}
              >
                or
              </Box>
              <Button
                color="primary"
                disabled={isPending}
                onClick={handleRegister}
                height={32}
                marginLeft="8px"
                marginRight="8px"
                iconStart={<UserPlus />}
              >
                Register
              </Button>
            </>
          )}
        </>
      )}
      {children}
    </Box>
  );
};

export default HeaderUserStatus;
